import { Component, Input, OnChanges, SimpleChanges, Output, EventEmitter, OnInit, ViewChildren, QueryList, Injector } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { WFSectionLocalResource, WFStepLocalResource, WfTaskLocalResource } from 'src/app/components/workflow/workflow';
import { Checklist } from '../../checklists';
import { ChecklistsService } from '../../checklists.service';
import { ChecklistSectionComponent } from './checklist-section/checklist-section.component';
import { utils } from 'src/app/modules/libs/utils';
import { Observable } from 'rxjs/internal/Observable';
import { Subscription } from 'rxjs';
import { BaseComponent } from 'src/app/common/base/base.component';
import { ComponentType } from 'src/app/common/enumerations/enumerations';
import { ConditionParser } from 'src/app/modules/libs/condition-parser';

@Component({
  selector: "checklist-sections",
  templateUrl: "./checklist-sections.component.html",
  styleUrls: ["./checklist-sections.component.scss"],
})
export class ChecklistSectionsComponent extends BaseComponent implements OnInit, OnChanges {
  @Input() currentChecklist?: Checklist | null;
  @Input() precheck?: boolean;
  @Input() fullHeight?: boolean;
  @Input() disabled?: boolean;

  @Output() checkShared = new EventEmitter<any>();

  sections!: WFSectionLocalResource[];
  scrolled?: boolean;
  updating: boolean = false;

  checklists?: Checklist[];
  checklistsFiltered?: Checklist[];
  checklists$!: Observable<Checklist[]>;
  checklistsSubs!: Subscription;

  @ViewChildren(ChecklistSectionComponent) public sectionComponents!: QueryList<ChecklistSectionComponent>;

  viewportBreadcrumb?: ChecklistViewportBreadcrumb;


  constructor(
    protected override injector: Injector,
    private route: ActivatedRoute,
    private service: ChecklistsService,
  ) { super(injector); }

  ngOnInit(): void {
    this.loadChecklists();
  }

  /**
   * Reloads the rendered section tree when checklist execution state changes.
   * @param changes Angular input changes for the checklist section list.
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentChecklist']) {
      if (changes['currentChecklist'].currentValue?.id != changes['currentChecklist'].previousValue?.id) {
        this.scrolled = false;
        this.scrollToTask();
      }
      // Status changes can reset task signatures without changing the checklist ID or section count.
      if (changes['currentChecklist'].currentValue !== changes['currentChecklist'].previousValue) {
        if (this.currentChecklist?.documentTypeID)
          this.setOpenDocument(this.currentChecklist.documentTypeID, this.currentChecklist.id);
        this.loadSections();
      }
    }
  }

  loadChecklists() {
    this.checklists$ = this.store.select(state => state.Checklists.data);
    this.checklistsSubs = this.checklists$.subscribe(data => {
      if (data?.length) {
        this.checklists = data;
        if (this.currentChecklist) {
          const checklist = this.checklists.find(c => c.id == this.currentChecklist?.id);
          if (checklist) {
            this.currentChecklist = checklist;
            this.loadSections();
          }
        }
      }
    });
  }

  async loadSections() {
    let checklist: Checklist | undefined;
    if (this.currentChecklist?.documentTypeID && this.checklists) {
      checklist = (await this.checkOpenDocument<Checklist>(this.currentChecklist?.documentTypeID, this.checklists))?.document;
    }

    if (checklist)
      this.currentChecklist = checklist as Checklist;

    this.sections = this.currentChecklist?.wfTable?.wfTableLocal?.wfSectionLocals?.sort((a, b) => utils.sort(a.order, b.order)) ?? [];
    const sectionTypes = Array.from(new Set(this.sections?.filter(s => s.type).map(s => s.type)));
    sectionTypes.map(t => {
      const sections = this.sections.filter(s => t && s.type == t);
      if (sections.length > 1) {
        let index = 1;
        sections.filter((s, i) => i != 0).map(s => { s.code = String.fromCharCode(96 + (index++)) });
      }
    });
    if (!this.sections) { this.sections = []; }
    this.service.setAllTasks(this.sections);
    // Apply persisted condition values before sections and steps determine their initial visibility.
    this.evaluateTaskConditions();
    setTimeout(() => this.updateViewportBreadcrumb());
  }

  /**
   * Returns sections containing at least one step and task visible in the current checklist status.
   */
  get visibleSections(): WFSectionLocalResource[] {
    return this.sections?.filter(section =>
      section.wfStepLocals?.some(step => this.isStepVisible(step))
    ) ?? [];
  }

  /**
   * Checks status visibility and condition-derived task visibility for a step.
   * @param step Step to evaluate.
   */
  private isStepVisible(step: WFStepLocalResource): boolean {
    const hidden = step.wfStepStatusLocals
      ?.find(status => status.status == this.currentChecklist?.checklistStatusID)?.hidden ?? false;
    const statusDisabled = !step.wfStepStatusLocals
      .map(status => status.status).includes(this.currentChecklist?.checklistStatusID);
    // Status-disabled Hide tasks should not keep an otherwise empty section visible.
    const hasVisibleTasks = step.wfTaskLocals?.some(task => {
      const disableInsteadOfHide = !!this.utils.JSONparse(task.configuration)?.hideDisable;
      return task.required !== false &&
        (!statusDisabled || !task.condition || disableInsteadOfHide);
    }) ?? false;
    return !hidden && hasVisibleTasks;
  }

  /**
   * Re-evaluates all conditions after checklist tasks load or a task changes.
   * Uses a second pass so workflow-wide conditions read settled task visibility and completion state.
   * @param changedTask Optional task containing the newly selected or persisted value.
   */
  evaluateTaskConditions(changedTask?: WfTaskLocalResource): void {
    const tasks = this.getChecklistTasks();
    if (!tasks.length || (changedTask && !tasks.some(task => task.id === changedTask.id)))
      return;

    // Conditions can reference task codes from other steps or sections.
    tasks.forEach(task => {
      task.isCompleted = this.utils.taskComplete(task);
      task.sigConditionResult = task.sigCondition
        ? tasks.find(conditionTask => conditionTask.code === task.sigCondition)?.wfTasks?.at(-1)?.signedBy?.id === this.currentUser?.id
        : false;
    });

    // Settle task-level visibility before workflow-wide conditions inspect prior completion state.
    ConditionParser.parseConditions(tasks);
    tasks.forEach(task => task.isCompleted = !task.required || this.utils.taskComplete(task));
    ConditionParser.parseConditions(tasks);
    this.applyPreviousTaskState(tasks);
    this.refreshRenderedTasks();
  }

  /**
   * Returns every task in checklist display order for cross-step condition evaluation.
   * @returns Flattened checklist task collection.
   */
  private getChecklistTasks(): WfTaskLocalResource[] {
    return [...(this.sections ?? [])]
      .sort((a, b) => utils.sort(a.order, b.order))
      .flatMap(section => [...(section.wfStepLocals ?? [])]
        .sort((a, b) => utils.sort(a.order, b.order))
        .flatMap(step => [...(step.wfTaskLocals ?? [])].sort((a, b) => utils.sort(a.order, b.order))));
  }

  /**
   * Updates completion and previous-task state after visibility conditions are applied.
   * @param tasks Ordered checklist tasks with evaluated required state.
   */
  private applyPreviousTaskState(tasks: WfTaskLocalResource[]): void {
    tasks.forEach(task => task.isCompleted = !task.required || this.utils.taskComplete(task));

    const requiredTasks = tasks.filter(task =>
      task.required &&
      task.wfTasks?.at(-1)?.enabled !== false &&
      (task.component?.type ?? 0) !== 2
    );

    tasks.forEach(task => {
      const currentIndex = requiredTasks.findIndex(requiredTask => requiredTask.id === task.id);
      task.previousTasksCompleted = currentIndex <= 0 ||
        requiredTasks.slice(0, currentIndex).every(previousTask => previousTask.isCompleted);
    });
  }

  /**
   * Refreshes currently rendered task signatures after their source tasks change.
   */
  private refreshRenderedTasks(): void {
    this.sectionComponents?.forEach(sectionComponent =>
      sectionComponent.stepsComponent?.stepComponents?.forEach(stepComponent =>
        stepComponent.tasksComponent?.refreshRenderedTasks()
      )
    );
  }

  scrollToTask() {
    if (!this.scrolled) {
      const id = this.route.snapshot.queryParams['task'];
      if (id) {
        const element = document.getElementById(id);
        if (element) {
          setTimeout(() => {
            element?.scrollIntoView({ behavior: "smooth" });
          }, 1000);
          this.scrolled = true;
        }
      } else {
        const element = document.getElementById("sections");
        const container = document.getElementById('scrollableContainer');

        if (element && container) {
          // Calculate the distance of the element from the top of the container
          const elementOffsetTop = element.offsetTop - 10;

          // Scroll the container to the element's position
          container.scrollTo({
            top: elementOffsetTop,
            behavior: 'smooth', // Optional for smooth scrolling
          });
          this.scrolled = true;
        }
      }
    }
  }

  trackBySection(_index: number, section: WFSectionLocalResource): number {
    return section.id;
  }

  /**
   * Updates the sticky breadcrumb with the task nearest the top of the checklist viewport.
   */
  updateViewportBreadcrumb(): void {
    const breadcrumb = this.getVisibleTaskBreadcrumb();
    if (!breadcrumb) {
      this.viewportBreadcrumb = undefined;
      return;
    }
    if (breadcrumb && !this.isSameBreadcrumb(breadcrumb, this.viewportBreadcrumb)) {
      // Keep the sticky context in sync with the user's scroll position.
      this.viewportBreadcrumb = breadcrumb;
    }
  }

  /**
   * Checks whether the rendered breadcrumb already matches the visible task context.
   * @param next Breadcrumb calculated from the current viewport.
   * @param current Breadcrumb currently displayed.
   */
  private isSameBreadcrumb(next: ChecklistViewportBreadcrumb, current?: ChecklistViewportBreadcrumb): boolean {
    return next.taskID === current?.taskID &&
      next.sectionLabel === current?.sectionLabel &&
      next.stepLabel === current?.stepLabel &&
      next.taskLabel === current?.taskLabel;
  }

  /**
   * Refreshes the sticky breadcrumb after expansion panels finish changing the rendered layout.
   */
  refreshViewportBreadcrumbAfterLayout(): void {
    setTimeout(() => this.updateViewportBreadcrumb());
    setTimeout(() => this.updateViewportBreadcrumb(), 250);
  }

  /**
   * Finds the visible task that should represent the current scroll position.
   */
  private getVisibleTaskBreadcrumb(): ChecklistViewportBreadcrumb | undefined {
    const container = document.getElementById('scrollableContainer');
    if (!container) {
      return undefined;
    }

    const containerRect = container.getBoundingClientRect();
    const breadcrumbOffset = this.getBreadcrumbOffset();
    const targetTop = containerRect.top + breadcrumbOffset + 8;
    let current: { distance: number; breadcrumb: ChecklistViewportBreadcrumb } | null = null;

    for (const context of this.getTaskContexts()) {
      const element = context.task.id ? document.getElementById(context.task.id.toString()) : null;
      if (!element) {
        continue;
      }

      const elementRect = element.getBoundingClientRect();
      if (elementRect.bottom <= targetTop || elementRect.top >= containerRect.bottom) {
        continue;
      }

      const distance = elementRect.top <= targetTop && elementRect.bottom >= targetTop
        ? 0
        : Math.abs(elementRect.top - targetTop);

      if (!current || distance < current.distance) {
        current = { distance, breadcrumb: this.getTaskBreadcrumb(context.section, context.step, context.task) };
      }
    }

    return current?.breadcrumb;
  }

  /**
   * Returns loaded task contexts that are currently rendered in display order.
   */
  private getTaskContexts(): SharedTaskContext[] {
    const contexts: SharedTaskContext[] = [];
    for (const section of this.visibleSections) {
      if (section.collapsible && section.collapsed) {
        continue;
      }
      for (const step of section.wfStepLocals ?? []) {
        if (!this.isStepVisible(step) || (step.collapsible && step.collapsed)) {
          continue;
        }
        for (const task of step.wfTaskLocals ?? []) {
          if (task.required !== false) {
            contexts.push({ section, step, task });
          }
        }
      }
    }
    return contexts;
  }

  /**
   * Formats the visible task context shown in the sticky breadcrumb.
   * @param section Section containing the current task.
   * @param step Step containing the current task.
   * @param task Task currently represented in the viewport.
   */
  private getTaskBreadcrumb(section: WFSectionLocalResource, step: WFStepLocalResource, task: WfTaskLocalResource): ChecklistViewportBreadcrumb {
    return {
      sectionLabel: this.getSectionBreadcrumbLabel(section),
      stepLabel: this.stripHtml(step.name || section.name),
      taskLabel: this.getTaskLabel(task),
      sectionID: section.id,
      stepID: step.id,
      taskID: task.id,
    };
  }

  /**
   * Returns the section label using the same visible-section numbering rendered in the header.
   * @param section Section containing the current task.
   */
  private getSectionBreadcrumbLabel(section: WFSectionLocalResource): string {
    const visibleIndex = this.visibleSections.findIndex(visibleSection => visibleSection.id === section.id);
    const sectionNumber = visibleIndex >= 0 ? visibleIndex + 1 : section.number;
    return `${sectionNumber}${section.code ?? ''}. ${this.stripHtml(section.name)}`.trim();
  }

  /**
   * Returns the best breadcrumb label available for a task.
   * @param task Task currently represented in the viewport.
   */
  private getTaskLabel(task: WfTaskLocalResource): string {
    const taskName = this.stripHtml(task.name3 || task.name).trim();
    if (taskName) {
      return taskName;
    }

    // Fall back to component metadata for component-only tasks that do not have a configured title.
    const componentName = this.stripHtml(task.component?.name).trim();
    if (componentName) {
      return componentName;
    }

    return task.componentID ? ComponentType[task.componentID] ?? `Component ${task.componentID}` : 'Task';
  }

  /**
   * Scrolls the current breadcrumb section header below the sticky context row.
   */
  scrollToBreadcrumbSection(): void {
    if (this.viewportBreadcrumb?.sectionID) {
      this.scrollHeaderIntoView(`section-header-${this.viewportBreadcrumb.sectionID}`);
    }
  }

  /**
   * Scrolls the current breadcrumb step header below the sticky context row.
   */
  scrollToBreadcrumbStep(): void {
    if (this.viewportBreadcrumb?.stepID) {
      this.scrollHeaderIntoView(`step-header-${this.viewportBreadcrumb.stepID}`);
    }
  }

  /**
   * Scrolls a checklist header into view while accounting for the sticky breadcrumb row.
   * @param elementID Header element id to scroll to.
   */
  private scrollHeaderIntoView(elementID: string): void {
    const element = document.getElementById(elementID);
    if (!element) {
      return;
    }

    const scrollParent = this.getScrollParent(element);
    this.scrollElementToViewportTop(element, scrollParent, 'smooth');
    setTimeout(() => {
      this.scrollElementToViewportTop(element, scrollParent, 'auto');
      this.updateViewportBreadcrumb();
    }, 250);
  }

  /**
   * Aligns an element to the top of the visible checklist area, below the sticky breadcrumb.
   * @param element Element that should be visible at the top.
   * @param scrollParent Scrollable parent that owns the element movement.
   * @param behavior Browser scroll behavior.
   */
  private scrollElementToViewportTop(element: HTMLElement, scrollParent: HTMLElement | Window, behavior: ScrollBehavior): void {
    const targetTop = this.getViewportHeaderTop();
    const distance = element.getBoundingClientRect().top - targetTop;
    if (Math.abs(distance) < 2) {
      return;
    }

    // Adjust after measuring the real rendered position so Material header nesting does not hide the target.
    scrollParent.scrollBy({ top: distance, behavior });
  }

  /**
   * Finds the active scroll parent for an element.
   * @param element Element whose scroll owner should be located.
   */
  private getScrollParent(element: HTMLElement): HTMLElement | Window {
    let parent = element.parentElement;
    while (parent) {
      const style = window.getComputedStyle(parent);
      const overflowY = style.overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && parent.scrollHeight > parent.clientHeight) {
        return parent;
      }
      parent = parent.parentElement;
    }
    return window;
  }

  /**
   * Returns the screen position where clicked breadcrumb targets should land.
   */
  private getViewportHeaderTop(): number {
    const container = document.getElementById('scrollableContainer');
    const containerTop = container?.getBoundingClientRect().top ?? 0;
    return containerTop + this.getBreadcrumbOffset() + 8;
  }

  /**
   * Reads the sticky breadcrumb height so the active task is chosen below the pinned row.
   */
  private getBreadcrumbOffset(): number {
    const breadcrumb = document.querySelector('.viewport-breadcrumb') as HTMLElement | null;
    return breadcrumb?.offsetHeight ?? 0;
  }

  checkSharedComponents(e: any) {
    const taskID = e?.taskID;
    const shieldingResourceID = e?.shieldingResourceID;
    if (!taskID) {
      return;
    }

    const target = this.findTaskContext(taskID);
    if (!target) {
      return;
    }

    // Open the target containers before scrolling so hidden shared components can render.
    this.expandTarget(target.section, target.step);
    setTimeout(() => this.scrollToSharedShielding(taskID, shieldingResourceID), 150);
  }

  /**
   * Finds the section and step containing a target checklist task.
   * @param taskID Workflow task local identifier to locate.
   */
  private findTaskContext(taskID: number): SharedTaskContext | null {
    for (const section of this.sections ?? []) {
      for (const step of section.wfStepLocals ?? []) {
        const task = step.wfTaskLocals?.find(task => task.id == taskID);
        if (task) {
          return { section, step, task };
        }
      }
    }
    return null;
  }

  /**
   * Expands the section and step containing the shared shielding target.
   * @param section Checklist section containing the target.
   * @param step Checklist step containing the target.
   */
  private expandTarget(section: WFSectionLocalResource, step: WFStepLocalResource): void {
    if (section.collapsible && section.collapsed) {
      section.collapsed = false;
      if (this.currentChecklist) {
        this.service.saveSectionExpanded(this.currentChecklist.id, section.id, false);
      }
    }

    if (step.collapsible && step.collapsed) {
      step.collapsed = false;
      if (this.currentChecklist) {
        this.service.saveStepExpanded(this.currentChecklist.id, step.id, false);
      }
    }
    this.refreshViewportBreadcrumbAfterLayout();
  }

  /**
   * Scrolls the checklist viewport to the shared shielding row when possible.
   * @param taskID Workflow task local identifier to scroll to.
   * @param shieldingResourceID Shared shielding resource identifier to center.
   * @param attempt Retry count while expanded content renders.
   */
  private scrollToSharedShielding(taskID: number, shieldingResourceID?: number, attempt: number = 0): void {
    const rowID = shieldingResourceID ? `shielding-${taskID}-${shieldingResourceID}` : null;
    const element = rowID ? document.getElementById(rowID) : document.getElementById(taskID.toString());
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => this.updateViewportBreadcrumb(), 250);
      return;
    }

    if (attempt < 10) {
      // Wait for collapsed sections/steps to render the target shielding row.
      setTimeout(() => this.scrollToSharedShielding(taskID, shieldingResourceID, attempt + 1), 100);
      return;
    }

    const taskElement = document.getElementById(taskID.toString());
    if (taskElement) {
      taskElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => this.updateViewportBreadcrumb(), 250);
    }
  }

}

interface SharedTaskContext {
  section: WFSectionLocalResource;
  step: WFStepLocalResource;
  task: WfTaskLocalResource;
}

interface ChecklistViewportBreadcrumb {
  sectionLabel: string;
  stepLabel: string;
  taskLabel: string;
  sectionID: number;
  stepID: number;
  taskID?: number;
}
